/**
 * The brand: every name, line and asset path that says whose product this is.
 *
 * ## This is the first file a rebrand edits
 *
 * `index.html` holds no brand strings of its own. It carries `{{brand.*}}`
 * placeholders, and `tools/brandHtml.ts` fills them from this object at build
 * time (and on the dev server) — so the page title, the share-preview tags,
 * the theme colour and the intro screen can never drift from what the app
 * itself says. An unknown placeholder fails the build rather than shipping
 * literally.
 *
 * Colours beyond `themeColor` are CSS tokens: see the BRAND block at the top
 * of `src/style.css`. Per-deployment settings (keys, Firebase project) are not
 * brand and live in `src/content/client.ts`.
 *
 * ## There is no venue, and that is the point
 *
 * World Garden belongs nowhere in particular. You plant where you are. So
 * there is no client name, no city, no site and no host here — only the
 * product and the studio that makes it. If a field naming a place ever comes
 * back, the copy that reads it has quietly acquired a venue.
 *
 * ## Constraints on this module
 *
 * It is imported by `vite.config.ts`, which runs in Node, and by the game's
 * entry chunk, which runs on a phone on cellular data. So: plain data, no
 * imports, no DOM, no Node, and nothing large.
 *
 * The Arabic string below has **not** had a native speaker's review. It is a
 * neutral replacement for one written for a venue name that is no longer
 * ours; treat it as provisional until somebody who reads Arabic has seen it
 * on the share plate — see `TEMPLATE.md`.
 */

export const BRAND = {
  /** The product. There is no client or venue above it. */
  name: 'World Garden',
  /** Provisional — awaiting native review. */
  nameAr: 'حديقة العالم',
  /** Short form, for tight places. */
  shortName: 'World Garden',

  /** The studio credit, printed on shared images. */
  studio: 'Simam Digital',

  /** One line, used in the page title and share tags. */
  tagline: 'Grow a Garden in AR',

  /** `<title>` and `og:title`. */
  title: 'World Garden — Grow a Garden in AR',
  /** `<meta name="description">`, for search results. */
  description:
    'Plant a garden wherever you happen to be — a pavement, a balcony, a living-room floor — and watch it grow in augmented reality.',
  /** `og:description` and `twitter:description`. Shorter, for link previews. */
  shareDescription: 'Plant a garden wherever you are, in augmented reality.',

  /**
   * Browser chrome colour on mobile. Match `--surface-800-rgb` in
   * `src/style.css`; the two are not linked automatically.
   */
  themeColor: '#12261c',

  /** The intro screen, which is the first thing anybody sees. */
  intro: {
    eyebrow: 'Simam Digital',
    title: 'Grow a garden',
    titleAccent: 'wherever you are',
    lede:
      'A garden takes root where you stand — a pavement, a balcony, a patch of living-room floor. Sow it, tend it, and watch it fill out in augmented reality.',
  },

  /** Shown before a fact card has set its own category. */
  factKicker: 'World Garden',

  /** The saved share image and the share sheet's title for it. */
  share: {
    fileName: 'world-garden.png',
    title: 'My World Garden',
    /** Top of the plate, tracked capitals. */
    masthead: 'WORLD GARDEN',
    /** Foot of the plate, tracked capitals. */
    footer: 'GROWN IN AR · SIMAM DIGITAL',
  },

  /**
   * The shared image's palette (`src/ui/card.ts`). Canvas cannot read CSS
   * tokens, so these repeat the BRAND block in `src/style.css` by hand:
   * `ink` is `--surface-900`, `accent`/`accentSoft` are `--accent`/
   * `--accent-soft`, `paper` is `--text`. Six-digit hex only.
   */
  plate: {
    ink: '#0e1f16',
    inkDeep: '#081310',
    accent: '#d9a441',
    accentSoft: '#f0cd85',
    paper: '#fbf8f1',
  },

  /**
   * Brand assets, in one place. Paths are as `index.html` and the app load
   * them: `public/` is served from the site root.
   *
   * There is no intro photograph. The one that used to be here was a
   * generated picture of a specific park, and a photograph of a place is a
   * claim about a place — so the intro screen paints its own backdrop in CSS
   * instead (`#screen-intro` in `src/style.css`). A replacement plate, if
   * ever wanted, has to be of nowhere.
   *
   * `ogImage` is optional; with it set the build adds `og:image` and a large
   * Twitter card. Use an absolute URL for it — link previews do not resolve
   * relative ones.
   */
  assets: {
    favicon: '/favicon.svg',
    ogImage: null as string | null,
  },
} as const;

export type Brand = typeof BRAND;
